// storage.js
import AsyncStorage from '@react-native-async-storage/async-storage';
import {redactCurrentUser} from '../demo/demoRedactor';
import {slimUserRestaurants} from './restaurantCache';
import {safeSetJson} from './storageQuota';

const USER_INFO_KEY = 'HG_USER_INFO';
const ACCESS_TOKEN_KEY = 'HG_ACCESS_TOKEN';
const DEVICE_TRUST_KEY = 'HG_DEVICE_TRUST_TOKEN';
const DEVICE_ID_KEY = 'HG_DEVICE_TRUST_ID';
const LOGOUT_REASON_KEY = 'HG_LOGOUT_REASON';

/**
 * Persist the signed-in user payload.
 *
 * The embedded `restaurants[]` is projected through `slimUserRestaurants` first:
 * `/api/user/me` populates each store in full, including the unbounded credit
 * ledgers and stored templates that blew the web storage quota
 * (`QuotaExceededError` on the following `restaurant` write). No reader of
 * `HG_USER_INFO` touches any of the dropped fields — see `restaurantCache`.
 *
 * Quota-guarded and never throwing: this is awaited on the login path, so a
 * failed cache write must not abort the sign-in.
 *
 * @param {Object|null|undefined} userInfo Full user payload (`/api/user/me`).
 * @returns {Promise<boolean>} true when the value was stored (or was unchanged).
 */
export const setUserInfo = async (userInfo) => {
    const { ok } = await safeSetJson(USER_INFO_KEY, slimUserRestaurants(userInfo), {
        label: 'HG_USER_INFO',
    });

    return ok;
};

export const getUserInfo = async () => {
    try {
        const userInfo = await AsyncStorage.getItem(USER_INFO_KEY);
        // Demo mode: fake the signed-in operator's name/e-mail wherever the app
        // reads the current user for display (headers, drawer, settings). No-op
        // when demo is off; auth-critical fields (_id/roles/restaurants) are kept
        // intact, and the stored copy stays real (only the returned copy is faked).
        return userInfo ? redactCurrentUser(JSON.parse(userInfo)) : null;
    } catch (error) {
        console.error('Error getting user info:', error);
    }
};

export const removeUserInfo = async () => {
    try {
        await AsyncStorage.removeItem(USER_INFO_KEY);
    } catch (error) {
        console.error('Error removing user info:', error);
    }
};

export const setAccessToken = async (token) => {
    try {
        await AsyncStorage.setItem(ACCESS_TOKEN_KEY, token);
    } catch (error) {
        console.error('Error setting access token:', error);

        return;
    }

    // Mirror the new token to the iOS widget App Group right away: the WidgetKit extension fetches with ITS copy, and a
    // token that only updates on the next snapshot publish leaves the widgets on a dead session ("looks broken").
    try {
        require('./widgets/widgetBridge').syncWidgetAuthToken().catch(() => {});
    } catch (_error) {
        // Widgets unavailable (web / tests) — ignore.
    }
};

export const getAccessToken = async () => {
    try {
        return await AsyncStorage.getItem(ACCESS_TOKEN_KEY);
    } catch (error) {
        console.error('Error getting access token:', error);
    }
};

export const removeAccessToken = async () => {
    try {
        await AsyncStorage.removeItem(ACCESS_TOKEN_KEY);
    } catch (error) {
        console.error('Error removing access token:', error);
    }
};

// ── "Remember this device" — 30-day 2FA bypass token ────────────────────────
export const setDeviceTrustToken = async (token) => {
    try {
        if (token) {
            await AsyncStorage.setItem(DEVICE_TRUST_KEY, token);
        }
    } catch (error) {
        console.error('Error setting device trust token:', error);
    }
};

export const getDeviceTrustToken = async () => {
    try {
        return await AsyncStorage.getItem(DEVICE_TRUST_KEY);
    } catch (error) {
        console.error('Error getting device trust token:', error);
        return null;
    }
};

export const removeDeviceTrustToken = async () => {
    try {
        await AsyncStorage.removeItem(DEVICE_TRUST_KEY);
    } catch (error) {
        console.error('Error removing device trust token:', error);
    }
};

// ── Logout reason — why the last session ended ──────────────────────────────
// Persisted just before the `restartApp` remount so the reason survives the full
// root re-render and the Login screen can show a professional banner explaining
// WHY the user is back (session expired / access revoked / device signed out /
// account inactive) instead of dropping them on a bare login form.
export const setLogoutReason = async (reason) => {
    try {
        if (reason) {
            await AsyncStorage.setItem(LOGOUT_REASON_KEY, String(reason));
        }
    } catch (error) {
        console.error('Error setting logout reason:', error);
    }
};

export const getLogoutReason = async () => {
    try {
        return await AsyncStorage.getItem(LOGOUT_REASON_KEY);
    } catch (error) {
        console.error('Error getting logout reason:', error);
        return null;
    }
};

export const removeLogoutReason = async () => {
    try {
        await AsyncStorage.removeItem(LOGOUT_REASON_KEY);
    } catch (error) {
        console.error('Error removing logout reason:', error);
    }
};

/**
 * Returns a stable per-install device id the trust token is bound to.
 * Generates and persists one the first time it is requested.
 */
export const getOrCreateDeviceTrustId = async () => {
    try {
        let id = await AsyncStorage.getItem(DEVICE_ID_KEY);

        if (!id) {
            id = `dev_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
            await AsyncStorage.setItem(DEVICE_ID_KEY, id);
        }

        return id;
    } catch (error) {
        console.error('Error resolving device trust id:', error);
        return null;
    }
};

